import posthog from 'posthog-js';

/**
 * The single seam between the app and PostHog.
 *
 * Nothing else imports `posthog-js`. Two reasons that matters:
 *
 *  - **Every function below is a no-op until `initAnalytics` succeeds**, and it
 *    only succeeds when `VITE_POSTHOG_KEY` is set. So the test suite stays
 *    network-free without mocking anything, and local `npm run dev` is silent
 *    by default.
 *  - Event names live in one typed union rather than as string literals scattered
 *    across the drawer components, so a rename is a compile error instead of a
 *    silently orphaned funnel in the PostHog UI.
 */

const KEY = import.meta.env['VITE_POSTHOG_KEY']?.trim() ?? '';
const HOST = import.meta.env['VITE_POSTHOG_HOST']?.trim() ?? 'https://us.i.posthog.com';

let ready = false;

/** Single definition of "this path is the admin console". */
export function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

/**
 * The admin console is operational tooling, not a prospect session, and none of
 * it belongs in the product analytics: an admin dialing numbers on someone
 * else's scenario is indistinguishable, in a funnel or a replay, from that
 * prospect exploring their own assumptions.
 *
 * Seeded from the entry URL rather than from a component, so it is already true
 * before `initAnalytics()` runs in main.tsx and before React mounts anything —
 * nothing can beat it to the first pageview. `Shell` keeps it in step with
 * client-side navigation, which is what makes it self-clearing: a flag that only
 * ever turned ON would silently kill analytics for the rest of the tab's life,
 * and a live meeting that captures nothing while looking completely normal is
 * the worst failure this app has.
 */
let adminMode = typeof window !== 'undefined' && isAdminPath(window.location.pathname);

export function setAdminMode(on: boolean): void {
  adminMode = on;
}

/** Test seam. */
export function isAdminMode(): boolean {
  return adminMode;
}

/** Every event this app emits. */
export type AnalyticsEvent =
  | 'scenario_opened'
  | 'unknown_code'
  | 'service_unreachable'
  | 'param_changed'
  | 'row_added'
  | 'row_removed'
  | 'scenario_reset'
  | 'view_exploded'
  | 'drawer_opened'
  | 'history_opened'
  | 'revision_restored'
  | 'restore_failed'
  | 'save_failed';

export function initAnalytics(): void {
  if (ready || !KEY) return;

  posthog.init(KEY, {
    api_host: HOST,
    /**
     * Only create a person profile once we know which prospect this is, which
     * happens when the canvas route identifies by access code. Anonymous hits on
     * the access-code entry box are not worth a profile.
     */
    person_profiles: 'identified_only',
    // Captured manually below: this is an SPA, so the automatic pageview would
    // only ever fire once per session.
    capture_pageview: false,
    autocapture: true,
    respect_dnt: true,
    /**
     * The kill switch, and the only part of it that is actually load-bearing.
     *
     * `before_send` runs inside `capture()` for EVERY event, which is the one
     * place the uninstrumented sources converge: autocapture clicks,
     * `$pageleave`, and the `$snapshot` payloads that carry session replay.
     * Gating `track()` stops none of those — the app never calls `capture()` for
     * any of them, and `maskAllInputs: false` below means replay would otherwise
     * ship an admin's keystrokes on a prospect's numbers.
     *
     * The flag is read at capture time, not at init time, so flipping
     * `adminMode` takes effect on the next event with no re-init and, crucially,
     * no persisted state. That rules out `posthog.opt_out_capturing()`, which
     * writes the opt-out to localStorage and would therefore follow this browser
     * into every later prospect session, silently.
     */
    before_send: (event) => (adminMode ? null : event),
    session_recording: {
      /**
       * Input values are deliberately NOT masked. The entire point of replay
       * here is seeing which assumptions a prospect reaches for — the numbers
       * they type ARE the signal.
       *
       * This is safe only because the tool holds zero PHI: synthetic aggregate
       * planning assumptions, never member-level data. That guardrail is what
       * this setting depends on. If it ever stops holding, set this to true.
       */
      maskAllInputs: false,
    },
  });

  ready = true;
}

export function track(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
  if (!ready || adminMode) return;
  posthog.capture(event, properties);
}

/**
 * The access code IS the prospect identity, so replays and funnels group per
 * prospect without any extra plumbing.
 */
export function identifyScenario(accessCode: string): void {
  /* NOT redundant with `before_send`. `posthog.identify()` writes distinct_id,
     $user_id and $device_id into PostHog's own localStorage BEFORE it captures
     $identify, so dropping the event would still leave an admin-visited access
     code as this browser's identity — and it would then be attached to every
     later non-admin event in the same browser. */
  if (!ready || adminMode) return;
  posthog.identify(accessCode, { access_code: accessCode });
}

export function capturePageview(path: string): void {
  if (!ready || adminMode) return;
  posthog.capture('$pageview', { $current_url: path });
}

/**
 * The session id travels with every scenario write, which is what lets a history
 * entry link to the replay of the edit that produced it. Returns null when
 * analytics is off, and the write simply carries no session id.
 */
export function getSessionId(): string | null {
  /* Not a capture, so `before_send` cannot reach it. Returning null here is what
     removes X-Session-Id from every admin write and ?s= from every admin beacon:
     apiConfig's writeHeaders() and authQuery() already omit the field when there
     is no session id, so neither of them needs to change. */
  if (!ready || adminMode) return null;
  try {
    return posthog.get_session_id() || null;
  } catch {
    // Recording may not have started yet. Not worth failing a save over.
    return null;
  }
}

/** Test seam: lets a spec assert the no-op behavior without a real init. */
export function isAnalyticsReady(): boolean {
  return ready;
}
